import { useApolloClient } from "@apollo/client";
import React from "react";
import * as Yup from "yup";
import {
  AddressInput,
  DesiredProjectInitialStatus,
  ProjectType,
  SetCustomFieldsInputValue,
  namedOperations,
} from "../../../clients/graphqlTypes.js";
import {
  useCreateCrmCompanyAsIndividualMutation,
  useCreateCrmCompanyMutation,
} from "../crm-companies/CrmCompany.generated.js";
import { useSetCustomFieldMutation } from "../custom-fields/EditCustomFieldModal.generated.js";
import {
  useAcceptPendingDocShareMutation,
  useAcceptPendingDocShare_CreateProjectMutation,
} from "./AcceptPendingDocShare.generated.js";
import { PendingDocShareProjectDraftClient } from "./projectDraft.js";

// Syntactic-only check: the orchestrator must not send a visible-but-malformed
// email to the CRM mutation, but it also must not silently swallow a
// syntactically valid email the mutation itself later rejects (that failure
// is surfaced to the user instead, per design decision 4/6).
function isSyntacticallyValidEmail(email: string): boolean {
  return Yup.string().email().isValidSync(email);
}

// Which step of the submit sequence failed. The UI translates the step into
// its own headline; `detail` carries the server's message when there is one
// (e.g. a rejected email), never copy written here.
export type AcceptPendingDocShareFailedStep = "contact" | "project";

// Which step after project creation failed, so the partial-failure dialog
// names the operation its Retry repeats.
export type AcceptPendingDocSharePartialFailureStep = "customFields" | "accept";

export type AcceptPendingDocShareSubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | {
      status: "error";
      failedStep: AcceptPendingDocShareFailedStep;
      detail: string | null;
      // The contact this session created before the project step failed, so
      // the form can show it as the chosen client instead of the candidate.
      createdContact: { crmOrganisationId: string; title: string } | null;
    }
  | {
      status: "partial-failure";
      failedStep: AcceptPendingDocSharePartialFailureStep;
      projectId: string;
      projectTitle: string;
      crmOrganisationId: string;
      // A retry is running; the dialog stays open instead of the form.
      retrying: boolean;
    }
  | { status: "success"; projectId: string; projectTitle: string };

type NewContactClient = Extract<
  PendingDocShareProjectDraftClient,
  { kind: "newContact" }
>;

interface CreatedProject {
  projectId: string;
  projectTitle: string;
  crmOrganisationId: string;
  // Written after creation; kept here so a retry repeats the write.
  customFieldValues: SetCustomFieldsInputValue[];
}

// A created project whose custom-field write or acceptance failed. The caller
// keeps it beyond the modal's lifetime, so accepting the share again after
// closing the partial-failure dialog resumes it instead of creating another
// project.
export interface UnfinishedPendingDocShareAcceptance {
  project: CreatedProject;
  failedStep: AcceptPendingDocSharePartialFailureStep;
}

interface Options {
  unfinishedAcceptance?: UnfinishedPendingDocShareAcceptance | null;
  onUnfinishedAcceptanceChange?: (
    unfinished: UnfinishedPendingDocShareAcceptance | null
  ) => void;
}

function partialFailureState(
  { project, failedStep }: UnfinishedPendingDocShareAcceptance,
  retrying: boolean
): AcceptPendingDocShareSubmitState {
  return {
    status: "partial-failure",
    failedStep,
    projectId: project.projectId,
    projectTitle: project.projectTitle,
    crmOrganisationId: project.crmOrganisationId,
    retrying,
  };
}

// Thrown for failures the orchestrator detects itself (no server message), so
// they surface only as the translated headline of the failed step.
class SubmitStepError extends Error {}

function errorDetail(err: unknown): string | null {
  if (err instanceof SubmitStepError) return null;
  return err instanceof Error && err.message ? err.message : null;
}

interface SubmitValues {
  title: string;
  type: ProjectType | null;
  categoryIds: string[];
  buildingAddress: AddressInput | null;
  client: PendingDocShareProjectDraftClient;
  description: string;
  budgetAmount: number | null;
  // Calendar dates as "YYYY-MM-DD", as the general project form sends them.
  earliestStart: string | null;
  deadline: string | null;
  urgent: boolean;
  teamIds: string[];
  initialStatus: DesiredProjectInitialStatus;
  // Already converted by the caller; empty when no custom field was entered.
  customFieldValues: SetCustomFieldsInputValue[];
}

// Orchestrates the quick-accept submit sequence: create the deferred contact
// at most once, then `createProject`, then -- only with entered values --
// `setCustomFields`, then `acceptDocShare`. The created CRM id and project are
// retained so a retry after a failed custom-field write or acceptance repeats
// only those two steps and never creates another project or contact.
export function useAcceptPendingDocShareOrchestrator(
  pendingDocShareId: string,
  { unfinishedAcceptance = null, onUnfinishedAcceptanceChange }: Options = {}
) {
  const client = useApolloClient();

  const [createCrmCompany] = useCreateCrmCompanyMutation({ client });
  const [createCrmCompanyAsIndividual] =
    useCreateCrmCompanyAsIndividualMutation({
      client,
    });
  // Deliberately selects only the created project's `id` and `title`.
  const [createProject] = useAcceptPendingDocShare_CreateProjectMutation({
    client,
  });
  const [setCustomFields] = useSetCustomFieldMutation({ client });
  const [acceptDocShare] = useAcceptPendingDocShareMutation({ client });

  // A share reopened after its partial failure starts in that dialog again.
  const [state, setState] = React.useState<AcceptPendingDocShareSubmitState>(
    () =>
      unfinishedAcceptance
        ? partialFailureState(unfinishedAcceptance, false)
        : { status: "idle" }
  );

  // The contact this modal session created for a `newContact` candidate,
  // keyed by that candidate's identity. It is reused only while the user keeps
  // that same candidate, so switching to another client after a later failure
  // never silently submits the earlier contact -- and the candidate itself is
  // never created twice.
  const createdContactRef = React.useRef<{
    candidate: NewContactClient;
    crmOrganisationId: string;
    title: string;
  } | null>(null);

  // Set once `createProject` succeeded, together with the client it was
  // created for and the custom-field values still to write, so a retry never
  // creates another project.
  const createdProjectRef = React.useRef<CreatedProject | null>(
    unfinishedAcceptance?.project ?? null
  );

  const createContact = async (
    candidate: NewContactClient
  ): Promise<{ id: string; title: string } | undefined> => {
    const email =
      candidate.email && isSyntacticallyValidEmail(candidate.email)
        ? candidate.email
        : undefined;

    if (candidate.contactType === "COMPANY") {
      const result = await createCrmCompany({
        variables: {
          input: { name: candidate.companyName ?? "", email },
        },
      });
      return result.data?.createCrmCompany.crmCompany;
    }

    if (!candidate.salutation) {
      // The draft mapper only derives an INDIVIDUAL candidate with a visible
      // salutation; never invent one here.
      throw new SubmitStepError("INDIVIDUAL candidate without salutation");
    }

    const result = await createCrmCompanyAsIndividual({
      variables: {
        input: {
          firstname: candidate.firstname ?? "",
          familyname: candidate.familyname ?? "",
          title: candidate.salutation,
          email,
        },
      },
    });
    return result.data?.createCrmCompanyAsIndividual.crmCompany;
  };

  const resolveClientCrmOrganisationId = async (
    clientState: PendingDocShareProjectDraftClient
  ): Promise<string> => {
    if (clientState.kind === "matched") {
      return clientState.crmOrganisationId;
    }

    if (clientState.kind === "none") {
      throw new SubmitStepError("Client is mandatory");
    }

    const created = createdContactRef.current;
    if (created && created.candidate === clientState) {
      return created.crmOrganisationId;
    }

    const crmCompany = await createContact(clientState);
    if (!crmCompany) throw new SubmitStepError("No CRM company returned");

    createdContactRef.current = {
      candidate: clientState,
      crmOrganisationId: crmCompany.id,
      title: crmCompany.title,
    };
    return crmCompany.id;
  };

  const createProjectOnce = async (
    values: SubmitValues,
    crmOrganisationId: string
  ): Promise<CreatedProject> => {
    const result = await createProject({
      variables: {
        input: {
          title: values.title,
          description: values.description,
          type: values.type ?? undefined,
          crmOrganisationId,
          buildingAddress: values.buildingAddress ?? undefined,
          categories: values.categoryIds,
          urgent: values.urgent,
          earliestStart: values.earliestStart ?? undefined,
          deadline: values.deadline ?? undefined,
          budgetAmount: values.budgetAmount ?? undefined,
          teamIds: values.teamIds,
          pageName: "Dashboard",
          desiredProjectInitialStatus: values.initialStatus,
        },
      },
      // As in the general project-creation form, plus the dashboard boxes
      // that list the new project after "Create only".
      refetchQueries: [
        namedOperations.Query.PlanningProjects,
        namedOperations.Query.ProjectsList,
        namedOperations.Query.DashboardProjects,
        namedOperations.Query.DashboardOpportunities,
      ],
    });

    const project = result.data?.createProject.project;
    if (!project) throw new SubmitStepError("No project returned");

    return {
      projectId: project.id,
      projectTitle: project.title,
      crmOrganisationId,
      customFieldValues: values.customFieldValues,
    };
  };

  const runSetCustomFields = async ({
    projectId,
    customFieldValues,
  }: CreatedProject): Promise<void> => {
    if (customFieldValues.length === 0) return;
    // Upserts per key, so repeating it on retry is harmless.
    await setCustomFields({
      variables: {
        input: {
          objectId: projectId,
          objectType: "Project",
          values: customFieldValues,
        },
      },
    });
  };

  const runAccept = async ({
    projectId,
    crmOrganisationId,
  }: CreatedProject): Promise<void> => {
    await acceptDocShare({
      variables: {
        input: {
          id: pendingDocShareId,
          acceptInProjectId: projectId,
          clientCrmOrganisationId: crmOrganisationId,
        },
      },
    });
  };

  // Finishes an already created project -- custom fields, then acceptance --
  // and records the outcome. Either failure leaves a retryable partial-success
  // state that records which of the two steps failed.
  const completeCreatedProject = async (
    project: CreatedProject
  ): Promise<{ projectId: string; projectTitle: string } | null> => {
    createdProjectRef.current = project;

    let failedStep: AcceptPendingDocSharePartialFailureStep = "customFields";
    try {
      await runSetCustomFields(project);
      failedStep = "accept";
      await runAccept(project);
    } catch {
      const unfinished = { project, failedStep };
      onUnfinishedAcceptanceChange?.(unfinished);
      setState(partialFailureState(unfinished, false));
      return null;
    }

    onUnfinishedAcceptanceChange?.(null);
    setState({
      status: "success",
      projectId: project.projectId,
      projectTitle: project.projectTitle,
    });
    return { projectId: project.projectId, projectTitle: project.projectTitle };
  };

  const submit = async (
    values: SubmitValues
  ): Promise<{ projectId: string; projectTitle: string } | null> => {
    setState({ status: "submitting" });

    const existingProject = createdProjectRef.current;
    if (existingProject) return completeCreatedProject(existingProject);

    let crmOrganisationId: string;
    try {
      crmOrganisationId = await resolveClientCrmOrganisationId(values.client);
    } catch (err) {
      setState({
        status: "error",
        failedStep: "contact",
        detail: errorDetail(err),
        createdContact: null,
      });
      return null;
    }

    let project: CreatedProject;
    try {
      project = await createProjectOnce(values, crmOrganisationId);
    } catch (err) {
      const created = createdContactRef.current;
      setState({
        status: "error",
        failedStep: "project",
        detail: errorDetail(err),
        createdContact:
          created && created.candidate === values.client
            ? {
                crmOrganisationId: created.crmOrganisationId,
                title: created.title,
              }
            : null,
      });
      return null;
    }

    return completeCreatedProject(project);
  };

  const retryAccept = async (): Promise<{
    projectId: string;
    projectTitle: string;
  } | null> => {
    const project = createdProjectRef.current;
    if (!project) {
      throw new Error("Nothing to retry");
    }

    // Stays in the partial-failure dialog, so the form (and the user's edits
    // in it) is not remounted while the remaining steps run.
    setState(prev =>
      prev.status === "partial-failure" ? { ...prev, retrying: true } : prev
    );
    return completeCreatedProject(project);
  };

  return { state, submit, retryAccept };
}
